import Image from '@atoms/Image/Image';
import LinkCard from '@atoms/LinkCard/LinkCard';
import LoadingOverlay from '@atoms/LoadingOverlay/LoadingOverlay';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import LoadMoreButton from '@atoms/LoadMoreButton/LoadMoreButton';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import { IOnlineRetailer } from '@models/IOnlineRetailer';
import onlineRetailersSearch from '@services/onlineRetailersSearch';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import updateDataLayer from '@utils/helpers/gtm';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useRefocus from '@utils/hooks/useRefocus';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

interface Props {
  readonly language: string;
  readonly loadMoreLabel: string;
  readonly onlineRetailersEndpoint: string;
  readonly text: string;
  readonly title: string;
}

const OnlineRetailers = ({
  language,
  loadMoreLabel,
  onlineRetailersEndpoint,
  text,
  title,
}: Props) => {
  const [retailers, setRetailers] = useState<IOnlineRetailer[]>([]);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);
  const [isBreakpointInitialized, setIsBreakpointInitialized] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const focusPlaceholderRef = useRefocus<HTMLAnchorElement>(retailers);
  const breakpoint = useBreakpoint();
  const isMobile =
    breakpoint === BreakpointEnum.Small || breakpoint === BreakpointEnum.Medium;

  const { setErrorBannerNotification } = useErrorNotificationStore();

  const handleClick = (retailerName: string) => {
    updateDataLayer({
      event: 'storeFinder',
      content_type: 'store_logo',
      store_name: retailerName,
    });
  };

  const fetchData = async () => {
    const pageSizes = {
      [BreakpointEnum.Small]: 4,
      [BreakpointEnum.Medium]: 8,
      [BreakpointEnum.Large]: 10,
      [BreakpointEnum.XLarge]: 18,
    };
    const pageSize = pageSizes[breakpoint as BreakpointEnum];

    try {
      if (retailers.length > 0) setIsLoading(true);

      const { items, hasNextPage } = await onlineRetailersSearch({
        endpoint: onlineRetailersEndpoint,
        language,
        from: retailers.length,
        pageSize,
      });

      setFocusedIndex(retailers.length);
      setRetailers((prevRetailers) => [...prevRetailers, ...(items || [])]);
      setShowLoadMoreButton(hasNextPage);
    } catch (error) {
      setErrorBannerNotification(
        ErrorBannerNotificationEnum.OnlineRetailersData
      );
    } finally {
      setIsInitialLoading(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isBreakpointInitialized) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBreakpointInitialized]);

  useEffect(() => {
    if (breakpoint) setIsBreakpointInitialized(true);
  }, [breakpoint]);

  return (
    <section
      className="component-padding text-black-100 bg-white"
      data-testid="CT-66-Online-Retailers"
    >
      <LoadingOverlay isLoading={isLoading} />

      <div className="container">
        {title && (
          <h2 className="h4" data-testid="CT-66-Online-Retailers-Title">
            {title}
          </h2>
        )}

        <div
          data-testid="CT-66-Online-Retailers-Text"
          className={classNames(
            'w-full lg:max-w-[571px] max-lg:text-grey-500',
            isMobile ? 'body-two' : 'body-one',
            {
              'mb-10 lg:mb-15': retailers.length > 0,
            }
          )}
          dangerouslySetInnerHTML={{ __html: text }}
        />

        {isInitialLoading && <LoadingSpinner extraClasses="min-h-[200px]" />}

        {retailers.length > 0 && (
          <div className="grid grid-cols-1 gap-5 md:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {retailers.map(({ retailerUrl, logo, retailerName }, index) => (
              <LinkCard
                key={`retailer-${retailerName}`}
                type="OUTLINE"
                link={{
                  name: retailerName,
                  url: retailerUrl,
                  target: '_blank',
                }}
                onClick={() => handleClick(retailerName)}
                className="h-[162px] md:h-[120px] xl:h-[162px] justify-center px-10 py-7.5"
                dataTestId={`CT-66-Online-Retailers-Link-Card-${index}`}
                ref={
                  focusedIndex > 0 && focusedIndex === index
                    ? focusPlaceholderRef
                    : null
                }
              >
                {logo && (
                  <Image
                    classes="w-full"
                    image={logo}
                    imageClasses="w-full h-full object-contain max-h-[96px]"
                    sizes="300px"
                    dataTestId={`CT-66-Online-Retailers-Link-Card-${index}-Logo`}
                  />
                )}

                {!logo && retailerName && (
                  <p
                    className="text-center"
                    data-test={`CT-66-Online-Retailers-Link-Card-${index}-Retailer-Name`}
                  >
                    {retailerName}
                  </p>
                )}
              </LinkCard>
            ))}
          </div>
        )}

        {loadMoreLabel && showLoadMoreButton && (
          <div className="mt-7.5">
            <LoadMoreButton
              loadMoreLabel={loadMoreLabel}
              onClick={() => {
                fetchData();
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default OnlineRetailers;
